/***
 *
 *   AUTHENTICATION
 *   Auth provider to manage auth functions throughout
 *   the application. <PrivateRoute> component to
 *   protect internal application routes from unauthenticated
 *   access.
 *
 **********/

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import jwt_decode from "jwt-decode";

// auth context
export const AuthContext = React.createContext();

const useAPI = require("components/lib").useAPI;
const Event = require("components/lib").Event;
const permissions = require("./permissions");

export function AuthProvider(props) {
  const cache = JSON.parse(localStorage.getItem("user"));
  const [user, setUser] = useState(cache);
  const auth = useAPI(user ? "/api/auth" : null);

  useEffect(() => {
    // update the auth status
    if (!auth.loading && auth.data) {
      auth.data.authenticated ? update(auth.data) : signout();
    }
  }, [auth]);

  function signin(res) {
    if (res.data) {
      localStorage.setItem("user", JSON.stringify(res.data));
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + res.data.token;
      Event("signin");
      console.log("User signed in:", JSON.stringify(res.data));

      if (!res.data.plan) {
        // preserve promo code query param if present
        const currentParams = new URLSearchParams(window.location.search);
        const promo = currentParams.get('promo');
        return (window.location = "/signup/plan" + (promo ? `?promo=${encodeURIComponent(promo)}` : ""));
      }

      if (res.data["2fa_mandatory"] && !res.data["2fa_enabled"]) {
        return (window.location = "/account/2fa");
      }
      console.log("User plan:", res.data)


      return (window.location = res.data.onboarded ? "/dashboard" : "/welcome");
    }
  }

  function pipedriveSignIn(res) {
    if (res.data) {
      localStorage.setItem("user", JSON.stringify(res.data));
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + res.data.token;
      Event("signin");

      const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      });

      const ufi = params?.ufi ? `?ufi=${params?.ufi}` : "";

      return (window.location = "/account/integrations/pipedrive" + ufi);
    }
  }

  function xeroSignUp(res) {
    if (res.data) {
      localStorage.setItem("user", JSON.stringify(res.data));
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + res.data.token;
      Event("signin");

      if (!res.data.plan) return (window.location = "/account/billing");

      return (window.location = "/dashboard");
    }
  }

  function pipedriveSignUp(res) {
    if (res.data) {
      localStorage.setItem("user", JSON.stringify(res.data));
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + res.data.token;
      Event("signup");

      const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      });

      const ufi = params?.userForeignIdentifier ? `?ufi=${params?.userForeignIdentifier}` : "";

      return (window.location = "/account/integrations/pipedrive" + ufi);
    }
  }

  async function signout() {
    axios({ method: "delete", url: "/api/auth" });
    localStorage.clear();
    return (window.location = "/signin");
  }

  async function switchAccount(id) {
    const res = await axios({
      method: "post",
      url: "/api/auth/switch",
      data: { account: id },
    });

    if (res.data) signin(res);
  }

  function update(data) {
    if (localStorage.getItem("user")) {
      let user = JSON.parse(localStorage.getItem("user"));
      for (let key in data) {
        if (Array.isArray(data[key])) {
          user[key] = data[key];
        } else if (typeof data[key] === "object") {
          for (let innerKey in data[key]) {
            user[key][innerKey] = data[key][innerKey];
          }
        } else {
          user[key] = data[key];
        }
      }

      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user: user,
        signin: signin,
        xeroSignUp: xeroSignUp,
        pipedriveSignUp: pipedriveSignUp,
        pipedriveSignIn: pipedriveSignIn,
        signout: signout,
        update: update,
        switchAccount: switchAccount,
        permission: permissions[user?.permission],
      }}
      {...props}
    />
  );
}

// custom route object checks for an auth token before
// rendering the route – redirects if token is not present
export function PrivateRoute(props) {
  const posthog = usePostHog();

  // check user exists
  const user = JSON.parse(localStorage.getItem("user"));
  const path = window.location.pathname;

  useEffect(() => {
    // Identify user with PostHog when token is available
    if (user?.token && posthog) {
      try {
        const decoded = jwt_decode(user.token);

        // Identify the user with PostHog
        posthog.identify(decoded.userId, {
          account_id: decoded.accountId,
          email: user.email,
          permission: user.permission,
          plan: user.plan,
          subscription: user.subscription,
          onboarded: user.onboarded,
          "2fa_enabled": user["2fa_enabled"],
          token_iat: decoded.iat,
          token_exp: decoded.exp,
        });
      } catch (error) {
        console.error("Error identifying user with PostHog:", error);
      }
    }
  }, [user?.token, posthog]);

  if (user?.token) {
     // user needs to setup 2FA if it's mandatory but not yet enabled, and not already on 2FA page
    if (user["2fa_mandatory"] && !user["2fa_enabled"] && path !== "/account/2fa")
      return <Navigate to="/account/2fa" />;

    if (permissions[user.permission][props.permission]) {
      console.log("user needs to setup 2FA", user, user["2fa_mandatory"]);
      // user has no plan
      if (!user.plan && path !== "/account/profile" && path !== "/signup/plan") {
        // preserve promo code query param if present
        const currentParams = new URLSearchParams(window.location.search);
        const promo = currentParams.get('promo');
        const planUrl = "/signup/plan" + (promo ? `?promo=${encodeURIComponent(promo)}` : "");
        return <Navigate to={planUrl} />;
      }

      // user has no subscription
      if (
        user.subscription !== "active" &&
        user.subscription !== "trialing" &&
        user.permission !== "master"
      ) {
        if (
          !path.includes("/account/billing") &&
          !path.includes("/signup/plan") &&
          !path.includes("/account/upgrade") &&
          !path.includes("/account") &&
          !path.includes("/account/profile") &&
          // a billing problem is exactly when someone needs to reach support.
          // prefix, not substring: a future /account/self-help-* route must
          // not silently fall out of the redirect
          !(path === "/help" || path.startsWith("/help/"))
        ) {
          return <Navigate to="/account/billing" />;
        }
      }

      // user is good
      return props.children;
    }
  }

  // user is not authenticated
  return <Navigate to="/signin" />;
}
